// Dynamic Theme System for 135 Villages
import { getCurrentClientId, getVillageTheme } from '../config/villageConfig';

export const applyVillageTheme = () => {
  const clientId = getCurrentClientId();
  
  // Safety check: if clientId is undefined, skip theme application
  if (!clientId) {
    console.warn('ClientId is undefined, skipping theme application');
    return;
  }
  
  const theme = getVillageTheme(clientId);
  
  // Apply CSS custom properties dynamically
  const root = document.documentElement;
  
  // Primary brand colors
  root.style.setProperty('--color-primary', theme.primaryColor);
  root.style.setProperty('--color-primary-dark', theme.darkColor);
  
  // Background colors
  root.style.setProperty('--color-bg-light', theme.lightBg);
  root.style.setProperty('--color-bg-gradient', theme.gradient);
  
  // Additional derived colors for consistency
  root.style.setProperty('--color-primary-light', theme.primaryColor);
  root.style.setProperty('--color-primary-light-alt', theme.primaryColor);
  
  // Footer and other elements that use primary dark
  root.style.setProperty('--color-footer-bg', theme.darkColor);
  
  // Header gradient
  root.style.setProperty('--color-header-gradient', 
    `linear-gradient(to right, ${theme.primaryColor}, ${theme.darkColor})`);
  
  // Shadow colors based on primary
  const primaryRgb = hexToRgb(theme.primaryColor);
  if (primaryRgb) {
    root.style.setProperty('--color-shadow-dark-green', 
      `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`);
    root.style.setProperty('--color-shadow-dark-green-alt', 
      `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
    root.style.setProperty('--color-shadow-dark-green-alt2', 
      `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.05)`);
  }
  
  // Store clientId for reference
  root.style.setProperty('--village-client-id', clientId.toString());
  
  console.log(`✅ Applied theme for Village ClientId: ${clientId}`, theme);
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Call this function when the app loads
export const initializeVillageTheme = () => {
  // Apply theme immediately
  applyVillageTheme();
  
  // Reapply theme if domain changes (unlikely but good for testing)
  window.addEventListener('popstate', applyVillageTheme);
};

// Theme presets are now centralized in villageConfig.js
// Use getVillageTheme(clientId) to get theme colors for any village
// All 66 villages have unique color schemes based on their clientId

const themeManager = { applyVillageTheme, initializeVillageTheme };

export default themeManager;